import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Badge,
  Button,
  ButtonGroup,
  IconButton,
  Divider,
  Flex,
  Input,
  List,
  ListItem,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Heading,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  Tooltip,
} from "@chakra-ui/react";
import { FaArrowLeft, FaPlus } from "react-icons/fa";
import { Formik } from "formik";
import {
  createApprovalPackage,
  fetchApprovalPackages,
  updateApprovalPackage,
} from "../../../src/_actions/plannerActions";
import { Clipboard } from "../../_utils/copyToClipboard";
import { analyticsTrackEvent } from "../../_utils/common";
import Preloader from "../social/Preloader/Preloader";
import PreloaderWrapper from "../social/Preloader/PreloaderWrapper";

const steps = {
  PACKAGES_LIST: 1,
  NEW_PACKAGE: 2,
  PACKAGE_CREATED: 3,
  PACKAGE_UPDATED: 4,
}

const ExternalApprovalModal = ({
  draftIds,
  typeName,
  onClose,
}) => {
  const { t } = useTranslation(["planner"]);
  const [loading, setLoading] = useState(true); 
  const [selectedPackage, setSelectedPackage] = useState(); 
  const [packages, setPackages] = useState([]);
  const [updatedPackageDetails, setUpdatedPackageDetails] = useState({});
  const [step, setStep] = useState(steps.PACKAGES_LIST); 

  const getPackages = useCallback(async () => {
    setLoading(true);

    try {
      const res = await fetchApprovalPackages();

      setPackages(res.data.list);

      if (!selectedPackage && res.data.list.length > 0) {
        // auto select first package on the list if no package has been selected before
        setSelectedPackage(res.data.list[0].id);
      }
    } catch {
      // handled in action;
    }
    setLoading(false);
  }, [setLoading, selectedPackage]);

  const createPackage = useCallback(async ({ name, description }) => {
    setLoading(true);
    try {
      const res = await createApprovalPackage({
        draftIds,
        description,
        name,
      });

      setUpdatedPackageDetails(res.data);
      setStep(steps.PACKAGE_CREATED);
    } catch {
      // handled in action;
    }

    analyticsTrackEvent("draftCreatedApprovalPackage");
    setLoading(false);
  }, [setLoading, draftIds]);

  const handleAddToPackage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await updateApprovalPackage({
        draftIds,
        packageId: selectedPackage,
      });

      setUpdatedPackageDetails(res.data);
      setStep(steps.PACKAGE_UPDATED);
    } catch {
      // handled in action;
    }

    setLoading(false);
    analyticsTrackEvent("draftUpdatedApprovalPackage");
  }, [setLoading, draftIds, selectedPackage]);

  const handlePackageSelect = useCallback((value) => {
    setSelectedPackage(value);
  }, [setSelectedPackage]);

  useEffect(() => {
    getPackages();
  }, [])

  return (
    <Modal
      autoFocus={false}
      returnFocusOnClose={false}
      initialFocusRef={() => null}
      isOpen
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay />
      {step === steps.PACKAGES_LIST && (
      <ModalContent>
        <ModalHeader
          paddingLeft="4"
          borderBottom="1px solid"
          borderColor="whiteAlpha.300"
        >
          {t("external_approval.heading", { type: typeName, count: draftIds.length })}
        </ModalHeader>
        <ModalBody p="4" position="relative">
          {loading && (
            <PreloaderWrapper>
              <Preloader />
            </PreloaderWrapper>
          )}
          <Flex justifyContent="space-between" alignItems="center" mb="4">
            <Box>
              <Heading size="sm" m="0">
                {t("external_approval.packages")}
              </Heading>
              <Text color="gray.200" fontSize="sm" as="span">{t("external_approval.packages_tip")}</Text>
            </Box>
            <Button size="sm" leftIcon={<FaPlus />} onClick={() => setStep(steps.NEW_PACKAGE)}>
              {t("add_new", { ns: "common" })}
            </Button>
          </Flex>
          <RadioGroup onChange={handlePackageSelect} value={selectedPackage}>
            <List spacing="3">
              {packages.map((pack) => (
                <ListItem
                  key={pack.id}
                  border="1px solid"
                  borderColor={pack.id === selectedPackage ? "gray.300" : "gray.400"}
                  borderRadius="lg"
                  px="4"
                  py="2"
                  onClick={() => setSelectedPackage(pack.id)}
                >
                  <Flex justifyContent="space-between" alignItems="center">
                    <Box>
                      <Flex mb="1" alignItems="center">
                        <Heading as="h4" size="sm" m="0">{pack.title}</Heading>
                        <Tooltip
                          label={`${t("external_approval.post_count_tip")}: ${pack.postCount}`}
                          hasArrow
                          placement="top"
                        >
                          <Badge ml="2">
                            {pack.postCount}
                          </Badge>
                        </Tooltip>
                      </Flex>
                      <Text color="gray.200" fontSize="sm">{pack.description}</Text>
                    </Box>
                    <Box>
                      <Radio
                        value={pack.id}
                        mt="2"
                      />
                    </Box>
                  </Flex>
                  <Box height={pack.id === selectedPackage ? "90px" : 0} overflow="hidden" transition="height 0.3s ease">
                  <Divider my="2" borderColor="gray.400" />
                  <Text color="gray.100" fontSize="sm" mb="2">
                    {t("external_approval.client_link")}
                  </Text>
                  <Flex alignItems="center" mb="2">
                    <Input value={pack.link} mr="4" size="sm" />
                    <Clipboard
                      value={pack.link}
                      withText={true}
                      variant="secondary"
                      size="sm"
                    />
                  </Flex>
                  </Box>
                </ListItem>
              ))}
            </List>
          </RadioGroup>
        </ModalBody>
        <ModalFooter
          p="4"
          borderTop="1px solid"
          borderColor="whiteAlpha.300"
        >
          <ButtonGroup size="sm" spacing="4">
            <Button
              onClick={onClose}
              colorScheme="primary"
              variant="secondary"
              disabled={loading}
            >
              {t("cancel", { ns: "common" })}
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              onClick={handleAddToPackage}
            >
              {t("external_approval.add_to_package")}
            </Button>
          </ButtonGroup>
        </ModalFooter>
        <ModalCloseButton />
      </ModalContent>
      )}
      {(step === steps.PACKAGE_CREATED || step === steps.PACKAGE_UPDATED) && (
      <ModalContent>
        <ModalHeader
          paddingLeft="4"
          borderBottom="1px solid"
          borderColor="whiteAlpha.300"
        >
          {step === steps.PACKAGE_CREATED ? t("external_approval.package_created") : t("external_approval.package_updated")} 
        </ModalHeader>
        <ModalBody p="4" fontSize="sm" position="relative">
          {loading && (
            <PreloaderWrapper>
              <Preloader />
            </PreloaderWrapper>
          )}
          <Text color="gray.300" mb="4">
            {t("external_approval.package_updated_tip", { type: typeName, count: draftIds.length })}
          </Text>
          <Text color="gray.100" mb="3">
            {t("external_approval.package_name")}
            <Text color="gray.300">
              {updatedPackageDetails.title}
            </Text>
          </Text>
          <Text color="gray.100" mb="3">
            {t("external_approval.package_description")}
            <Text color="gray.300">
              {updatedPackageDetails.description}
            </Text>
          </Text>
          <Text color="gray.100" mb="1">
            {t("external_approval.package_link")}
          </Text>
          <Flex alignItems="center">
            <Input value={updatedPackageDetails.linkToReview} mr="4" size="sm" />
            <Clipboard
              value={updatedPackageDetails.linkToReview}
              withText={true}
              variant="secondary"
              size="sm"
            />
          </Flex>
        </ModalBody>
        <ModalFooter
          p="4"
          borderTop="1px solid"
          borderColor="whiteAlpha.300"
        >
          <ButtonGroup size="sm" spacing="4">
            <Button
              colorScheme="primary"
              variant="solid"
              onClick={onClose}
            >
              {t("return", { ns: "common" })}
            </Button>
          </ButtonGroup>
        </ModalFooter>
        <ModalCloseButton />
      </ModalContent>
      )}
      {step === steps.NEW_PACKAGE && (
      <Formik
        initialValues={{ name: '', description: '' }}
        validate={values => {
          const errors = {};
          if (!values.name) {
            errors.name = t("field_required", { ns: "common"});
          }
          return errors;
        }}
        onSubmit={createPackage}
      >
        {({
          values,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
        <form onSubmit={handleSubmit}>
          <ModalContent>
            <ModalHeader
              paddingLeft="4"
              borderBottom="1px solid"
              borderColor="whiteAlpha.300"
              pb="2"
            >
              <IconButton
                icon={<FaArrowLeft />}
                size="sm"
                variant="ghost"
                onClick={() => setStep(steps.PACKAGES_LIST)}
                mr="2"
                mt="-1"
                mb="0"
              />
              {t("external_approval.new_package")}
            </ModalHeader>
            <ModalBody p="4" position="relative">
              {loading && (
                <PreloaderWrapper>
                  <Preloader />
                </PreloaderWrapper>
              )}
              <FormControl isInvalid={errors.name} mb="4">
                <FormLabel htmlFor="packageName">
                  {t("external_approval.package_name")}
                </FormLabel>
                <Input
                  size="sm"
                  placeholder={t("external_approval.package_name")}
                  id="packageName"
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                />
                {errors.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="packageDescription">
                  {t("external_approval.package_description")}
                </FormLabel>
                <Textarea
                  id="packageDescription"
                  placeholder={t("external_approval.package_description_tip")}
                  resize="none"
                  size="sm"
                  name="description"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.description}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter
              p="4"
              borderTop="1px solid"
              borderColor="whiteAlpha.300"
            >
              <ButtonGroup size="sm" spacing="4">
                <Button
                  colorScheme="primary"
                  variant="secondary"
                  disabled={loading}
                  onClick={() => setStep(steps.PACKAGES_LIST)}
                >
                  {t("go_back", { ns: "common" })}
                </Button>
                <Button
                  colorScheme="primary"
                  variant="solid"
                  type="submit"
                    disabled={isSubmitting}
                >
                  {t("external_approval.add_package")}
                </Button>
              </ButtonGroup>
            </ModalFooter>
            <ModalCloseButton />
          </ModalContent>
        </form>
      )}
      </Formik>
      )}
    </Modal>
  );
};

ExternalApprovalModal.propTypes = {
  draftIds: PropTypes.array.isRequired,
};

export default ExternalApprovalModal;
